import { AxiosResponse } from "axios";
import { useApiRequest } from "../helper/api";
import { useRef } from "react";

import {
  ActivityResponseModel,
  ApproveRejectPayload,
  ApproverTimesheetPayload,
  approverTimesheetResponseData,
  CreateUpdateTimeSheetPayload,
  DiscrepancyResponse,
  getTimeheetdetailResponseModel,
  getTimesheetResponsePayload,
  GetutilisationResponse,
  leaveReportPayload,
  LeaveReportResponse,
  MovementRegisterResponse,
  myTimesheetListResponse,
  MyTimesheetStatusResponse,
  ReportPayload,
  ReportResponseList,
  utilisationPayload,
  EmployeeSkillsResponse,
  ALlEmployeeskillResponse,
  EmployeeAssignmentDirectoryReportItem,
} from "../model/timesheet";
import { toast } from "react-toastify";

const useTimeSheetApi = () => {
  const { fetchData, fetchDataExport } = useApiRequest();
  // Cache to dedupe repeated timesheet-by-date requests within a short window
  const timesheetCacheRef = useRef<Map<string, { timestamp: number; data: getTimeheetdetailResponseModel | null }>>(new Map());
  const CACHE_TTL_MS = 3000; // 3 seconds TTL is enough to absorb StrictMode double-mounts

  const fetchTimesheetList = async (
    payload: MyTimesheetStatusResponse,
  ): Promise<myTimesheetListResponse | null> => {
    const response = await fetchData("/reports/get_timedataby_status", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchGetTimesheet = async (
    payload: getTimesheetResponsePayload,
  ): Promise<getTimeheetdetailResponseModel | null> => {
    const cacheKey = `${payload.user_id}|${payload.start_date}|${payload.end_date}`;
    const cached = timesheetCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }

    const response = await fetchData("/reports/get_timesheet_data_by_date", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      const data = response.data as getTimeheetdetailResponseModel;
      timesheetCacheRef.current.set(cacheKey, { timestamp: Date.now(), data });
      return data;
    }
    return null;
  };

  const fetchLeaveReportList = async (payload: leaveReportPayload): Promise<LeaveReportResponse[] | null> => {
    const response = await fetchData("/reports/get_leave_report", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchMovementRegisterReportList = async (
    payload: leaveReportPayload,
  ): Promise<MovementRegisterResponse[] | null> => {
    const response = await fetchData("/reports/get_movement_register/", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchDiscrepancyReportList = async (
    payload: leaveReportPayload,
  ): Promise<DiscrepancyResponse[] | null> => {
    const response = await fetchData("/reports/active_users_not_assigned_to_projects", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchGetPromiseTimesheet = async (
    payload: getTimesheetResponsePayload,
  ): Promise<getTimeheetdetailResponseModel | null> => {
    const cacheKey = `${payload.user_id}|${payload.start_date}|${payload.end_date}`;
    const cached = timesheetCacheRef.current.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
      return cached.data;
    }

    const response = await fetchData(
      "/reports/get_timesheet_data_by_date",
      {
        method: "POST",
        data: payload,
      },
    );
    if (response?.status) {
      const data = response.data as getTimeheetdetailResponseModel;
      timesheetCacheRef.current.set(cacheKey, { timestamp: Date.now(), data });
      return data;
    }
    return null;
  };

  const fetchGetPromiseTimesheetWithExportreport = async (
    payload: getTimesheetResponsePayload,
  ): Promise<getTimeheetdetailResponseModel | null> => {
    const response = await fetchDataExport("/reports/get_timesheet_data_by_date", {
      method: "POST",
      data: payload,
    });

    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchProjectActivities = async (userID: number): Promise<ActivityResponseModel[] | null> => {
    const response = await fetchData("/reports/get_project_activities_by_user_id", {
      method: "POST",
      data: { user_id: userID },
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchtimesheetListFronApprover = async (
    payload: ApproverTimesheetPayload,
  ): Promise<approverTimesheetResponseData | null> => {
    const response = await fetchData("/reports/get_timesheet_for_approval", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchtimesheetReportList = async (payload: ReportPayload): Promise<ReportResponseList | null> => {
    const response = await fetchData("/reports/get_missing_timesheet_data", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };
  const createReport = async (
    createReportPayload: CreateUpdateTimeSheetPayload[],
  ): Promise<AxiosResponse | null> => {
    const response = await fetchData("/reports/create_update_report", {
      method: "POST",
      data: createReportPayload,
    });
    if (response?.status) {
      toast.success(response.data);
    }
    return response?.data;
  };

  const approveRejectReport = async (payload: ApproveRejectPayload): Promise<AxiosResponse | null> => {
    const response = await fetchData("/reports/approve_reject_timesheet", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      toast.success(response.data);
    }
    return response?.data;
  };

  const fetchUtilisationReport = async (
    payload: utilisationPayload,
  ): Promise<GetutilisationResponse[] | null> => {
    const response = await fetchData("/reports/get_utilisation_report", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchGetEmployeeskill = async (): Promise<ALlEmployeeskillResponse | null> => {
    const response = await fetchData("/skills/get_empskills", {
      method: "GET",
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchSaveEmployeeskill = async (payload: EmployeeSkillsResponse): Promise<unknown | null> => {
    const response = await fetchData("/skills/save_empskills", {
      method: "POST",
      data: payload,
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchEmployeeskillReport = async (): Promise<unknown | null> => {
    const response = await fetchData("/skills/get_skills_reports", {
      method: "get",

    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  const fetchEmployeeAssignmentDirectoryReport = async (): Promise<EmployeeAssignmentDirectoryReportItem[] | null> => {
    const response = await fetchData("/reports/get_employee_assignment_directory", {
      method: "POST",
    });
    if (response?.status) {
      return response.data;
    }
    return null;
  };

  return {
    fetchTimesheetList,
    fetchProjectActivities,
    fetchGetTimesheet,
    createReport,
    approveRejectReport,
    fetchGetPromiseTimesheet,
    fetchtimesheetListFronApprover,
    fetchtimesheetReportList,
    fetchLeaveReportList,
    fetchMovementRegisterReportList,
    fetchDiscrepancyReportList,
    fetchGetPromiseTimesheetWithExportreport,
    fetchUtilisationReport,
    fetchGetEmployeeskill,
    fetchSaveEmployeeskill,
    fetchEmployeeskillReport,
    fetchEmployeeAssignmentDirectoryReport,
  };
};

export default useTimeSheetApi;
